import React, { useCallback, useEffect, useState } from 'react';
import { sendMusteriIzinOtp, verifyMusteriIzinOtp } from '../services/customersService';

const STORAGE_PREFIX = 'hlm-musteri-izin-otp:';

const formatTelefon = (tel) => {
  const d = String(tel || '').replace(/\D/g, '');
  if (d.length === 10) return `0${d}`;
  return tel || '';
};

const storageKey = (musteriId) => `${STORAGE_PREFIX}${musteriId}`;

const loadOtpSession = (musteriId) => {
  if (!musteriId) return null;
  try {
    const raw = sessionStorage.getItem(storageKey(musteriId));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.expiresAt) return null;
    return data;
  } catch {
    return null;
  }
};

const saveOtpSession = (musteriId, data) => {
  if (!musteriId) return;
  try {
    sessionStorage.setItem(storageKey(musteriId), JSON.stringify(data));
  } catch {
    /* ignore */
  }
};

const clearOtpSession = (musteriId) => {
  if (!musteriId) return;
  try {
    sessionStorage.removeItem(storageKey(musteriId));
  } catch {
    /* ignore */
  }
};

const remainingFromExpiresAt = (expiresAt) => {
  if (!expiresAt) return 0;
  return Math.max(0, Math.ceil((Number(expiresAt) - Date.now()) / 1000));
};

const MusteriIzinDogrulamaModal = ({
  isOpen,
  musteri,
  izinBaglam,
  onSuccess,
  onClose,
}) => {
  const [otpKod, setOtpKod] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [expiresAt, setExpiresAt] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const musteriId = musteri?.id || musteri?._id;
  const telefon = musteri?.telefon || musteri?.phone || '';

  const applySession = useCallback((session) => {
    if (!session) {
      setOtpSent(false);
      setExpiresAt(null);
      setCountdown(0);
      setOtpKod('');
      return;
    }
    const remaining = remainingFromExpiresAt(session.expiresAt);
    setOtpSent(true);
    setExpiresAt(session.expiresAt);
    setCountdown(remaining);
    setOtpKod(typeof session.otpKod === 'string' ? session.otpKod.replace(/\D/g, '').slice(0, 6) : '');
  }, []);

  // Modal açılınca / müşteri değişince kayıtlı OTP oturumunu yükle
  useEffect(() => {
    if (!isOpen || !musteriId) return;
    setError('');
    setLoading(false);
    const session = loadOtpSession(musteriId);
    if (session && remainingFromExpiresAt(session.expiresAt) > 0) {
      applySession(session);
      return;
    }
    if (session) {
      clearOtpSession(musteriId);
    }
    // Süre dolmuşsa input adımını koruma — yeniden SMS gerekir
    setOtpSent(false);
    setExpiresAt(null);
    setCountdown(0);
    setOtpKod('');
  }, [isOpen, musteriId, applySession]);

  // Absolute expiresAt üzerinden geri sayım (modal kapalıyken de süre akar)
  useEffect(() => {
    if (!otpSent || !expiresAt) return undefined;
    const tick = () => {
      const remaining = remainingFromExpiresAt(expiresAt);
      setCountdown(remaining);
      if (remaining <= 0) {
        setExpiresAt(null);
        if (musteriId) clearOtpSession(musteriId);
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [otpSent, expiresAt, musteriId]);

  // Kod yazıldıkça session'a yaz (modal kapanıp açılınca kalsın)
  useEffect(() => {
    if (!musteriId || !otpSent || !expiresAt) return;
    const remaining = remainingFromExpiresAt(expiresAt);
    if (remaining <= 0) return;
    saveOtpSession(musteriId, { expiresAt, otpKod });
  }, [musteriId, otpSent, expiresAt, otpKod]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !loading) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, loading]);

  const handleSendOtp = async () => {
    if (!musteriId) return;
    setError('');
    setLoading(true);
    try {
      const res = await sendMusteriIzinOtp(musteriId);
      if (res?.basarili === false) {
        setError(res.mesaj || 'SMS gönderilemedi');
        return;
      }
      const sure = Number(res?.veri?.gecerlilikSuresiSaniye) || 120;
      const nextExpiresAt = Date.now() + sure * 1000;
      setOtpSent(true);
      setExpiresAt(nextExpiresAt);
      setCountdown(sure);
      setOtpKod('');
      saveOtpSession(musteriId, { expiresAt: nextExpiresAt, otpKod: '' });
    } catch (err) {
      setError(err?.message || err?.mesaj || 'SMS gönderilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!musteriId) return;
    setError('');
    if (!/^[0-9]{6}$/.test(otpKod.trim())) {
      setError('6 haneli doğrulama kodunu giriniz');
      return;
    }
    setLoading(true);
    try {
      const res = await verifyMusteriIzinOtp(musteriId, otpKod.trim(), izinBaglam);
      if (res?.basarili === false) {
        setError(res.mesaj || 'Doğrulama başarısız');
        return;
      }
      clearOtpSession(musteriId);
      setOtpSent(false);
      setExpiresAt(null);
      setCountdown(0);
      setOtpKod('');
      onSuccess?.(res?.veri?.musteri || res?.veri);
      onClose?.();
    } catch (err) {
      setError(err?.message || err?.mesaj || 'Doğrulama başarısız');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const adSoyad = [musteri?.adi || musteri?.name, musteri?.soyadi || musteri?.surname].filter(Boolean).join(' ');

  return (
    <div className="modal-overlay" role="presentation" onClick={loading ? undefined : onClose}>
      <div
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="musteri-izin-modal-title"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '520px' }}
      >
        <div className="modal-header">
          <h2 id="musteri-izin-modal-title">İletişim izni doğrulama</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} disabled={loading} aria-label="Kapat">
            <i className="fas fa-times" aria-hidden />
          </button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{
            padding: '12px 14px',
            borderRadius: '10px',
            background: '#eef2ff',
            border: '1px solid #c7d2fe',
            color: '#3730a3',
            fontSize: '14px',
          }}
          >
            <strong>{adSoyad || 'Müşteri'}</strong>
            {telefon && (
              <span style={{ display: 'block', marginTop: '4px', opacity: 0.9 }}>
                {formatTelefon(telefon)}
              </span>
            )}
          </div>

          <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-gray)', lineHeight: 1.5 }}>
            Müşteriye KVKK ve açık rıza metinlerini iletin. Onaylıyorsa doğrulama kodunu paylaşmasını isteyin.
          </p>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <a
              href="/kvkk"
              target="_blank"
              rel="noopener noreferrer"
              className="secondary-btn"
              style={{ textDecoration: 'none', fontSize: '13px' }}
            >
              <i className="fas fa-external-link-alt" style={{ marginRight: '6px' }} />
              KVKK metni
            </a>
            <a
              href="/acik-riza"
              target="_blank"
              rel="noopener noreferrer"
              className="secondary-btn"
              style={{ textDecoration: 'none', fontSize: '13px' }}
            >
              <i className="fas fa-external-link-alt" style={{ marginRight: '6px' }} />
              Açık rıza beyanı
            </a>
          </div>

          {!otpSent ? (
            <button type="button" className="primary-btn" onClick={handleSendOtp} disabled={loading || !musteriId}>
              {loading ? 'Gönderiliyor…' : 'SMS Gönder'}
            </button>
          ) : (
            <>
              <div style={{ fontSize: '13px', color: '#6366f1' }}>
                Kod gönderildi
                {countdown > 0 ? (
                  <span>
                    {' '}
                    —
                    {' '}
                    {countdown}
                    sn
                  </span>
                ) : (
                  <span>
                    {' '}
                    — süre doldu
                  </span>
                )}
              </div>
              <label style={{ display: 'block' }}>
                <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
                  Doğrulama kodu
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpKod}
                  onChange={(e) => setOtpKod(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '20px',
                    letterSpacing: '6px',
                    textAlign: 'center',
                    borderRadius: '8px',
                    border: '1px solid #c7d2fe',
                  }}
                />
              </label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={handleSendOtp}
                  disabled={loading || countdown > 0}
                >
                  Tekrar gönder
                </button>
                <button type="button" className="primary-btn" onClick={handleVerify} disabled={loading}>
                  {loading ? 'Doğrulanıyor…' : 'Doğrula'}
                </button>
              </div>
            </>
          )}

          {error && (
            <div style={{ color: '#dc2626', fontSize: '13px' }}>{error}</div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="secondary-btn" onClick={onClose} disabled={loading}>
            Vazgeç
          </button>
        </div>
      </div>
    </div>
  );
};

export default MusteriIzinDogrulamaModal;
